/* @flow */

import * as React from 'react';
import ReactTooltip from 'react-tooltip';

import classNames from 'classnames';
import {temporaryHtmlToString} from 'nutshell-core/list-items/temporary-html-to-string';

import {Avatar} from '../avatar';
import {AccountAvatar} from '../avatar/account-avatar';
import {colors} from '../colors';
import {LinkedEntity} from '../linked-entity';
import {LoadingText, LoadingCircle} from '../loading-placeholder';

import './peep-two-line.module.css';

type Props = {
    name: string,
    avatarUrl: ?string,
    initials: ?string,
    htmlUrl?: string,
    type: ?string,
    relatedEntityName: ?string,
    isLinkable?: boolean,
    isLoading?: boolean,
    shouldClientRoute?: boolean,
    shouldCenterAlignSingleRow?: boolean,
    rightContent?: ?React.Node,
    size?: 'mini' | 'normal',
};

export function PeepTwoLine(props: Props) {
    const {size = 'normal'} = props;

    React.useEffect(() => {
        ReactTooltip.rebuild();
    }, []);

    if (props.isLoading) {
        return (
            <div styleName='peep-two-line'>
                <div styleName='avatar'>
                    <LoadingCircle size={32} color={colors.offWhiteDk} />
                </div>
                <div styleName='peep-info'>
                    <div styleName='top-row'>
                        <LoadingText height={13} color={colors.offWhiteDk} />
                    </div>
                    <div styleName='bottom-row'>
                        <LoadingText height={13} color={colors.offWhiteMd} />
                    </div>
                </div>
            </div>
        );
    }

    const twoLineStyleNames = classNames('peep-two-line', {
        'peep-two-line--mini': size === 'mini',
    });

    const sanitizedName = temporaryHtmlToString(props.name) || props.name;
    const relatedEntityType = props.type === 'contacts' ? 'accounts' : 'contacts';

    // Loading state has no type, just satisfying flow
    if (!props.type) {
        return null;
    }

    return (
        <div styleName={twoLineStyleNames}>
            <div styleName='avatar'>
                {props.type === 'accounts' ? (
                    <AccountAvatar avatarUrl={props.avatarUrl} />
                ) : (
                    <Avatar
                        type={props.type}
                        initials={props.initials}
                        avatarUrl={props.avatarUrl}
                        size={size === 'mini' ? 24 : 32}
                    />
                )}
            </div>
            <div styleName='peep-info'>
                <div
                    styleName={
                        props.shouldCenterAlignSingleRow ? 'top-row--align-single' : 'top-row'
                    }
                >
                    <LinkedEntity
                        name={
                            sanitizedName ? (
                                <span
                                    style={{
                                        textOverflow: 'ellipsis',
                                        overflow: 'hidden',
                                    }}
                                >
                                    {sanitizedName}
                                </span>
                            ) : undefined
                        }
                        htmlUrl={props.htmlUrl}
                        isLinkable={props.isLinkable}
                        shouldClientRoute={props.shouldClientRoute}
                        stopPropagation={true}
                        type={props.type}
                    />
                    {props.rightContent}
                </div>
                {props.relatedEntityName ? (
                    <div styleName='bottom-row'>
                        <div styleName='associated'>
                            <LinkedEntity
                                name={props.relatedEntityName}
                                type={relatedEntityType}
                                isLinkable={false}
                                useEntityColor={true}
                            />
                        </div>
                    </div>
                ) : undefined}
            </div>
        </div>
    );
}
